import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import {
    getListViewByVisibleRange,
    createViewData,
    deleteViewData,
    updateViewData,
    getViewById
} from '@/service/viewService';
import { getHelpdeskDrilldownDataAndSugguestionList } from './HelpDeskTicketListHandler';
import { isValidViewName, scrollToView } from '@/component/ticketKnowledge/handler/ViewHandler';

export const getVisibleRangeByVisibleType = (visibleType, userId, projectId) => {
    let visibleRange = '';
    switch (visibleType) {
        case Constant.VISIBILITY_SETTING_TYPE.PRIVATE:
            visibleRange = userId;
            break;
        case Constant.VISIBILITY_SETTING_TYPE.PROJECT:
            visibleRange = projectId;
            break;

        default:
            break;
    }

    return visibleRange;
};

export const sortListViewByOrder = (listView, userId) => {
    return listView.sort((viewA, viewB) => {
        return JSON.parse(viewA?.sortData)[userId] - JSON.parse(viewB?.sortData)[userId];
    });
};

export const getHelpDeskListViews = async (
    API, 
    setIsInitView, 
    setViewSelected, 
    setViewStructure, 
    setListView, 
    userId, 
    projectSelectedId, 
    visibleType, 
    definitionView,
    getDefaultDrilldownParams = {}
) => {
    if (!visibleType || !projectSelectedId) return;

    let [privateViews, projectViews] = await Promise.all([
        getListViewByVisibleRange(
            API,
            projectSelectedId,
            userId,
            Constant.VISIBILITY_SETTING_TYPE.PRIVATE,
            definitionView
        ),
        getListViewByVisibleRange(
            API,
            projectSelectedId,
            projectSelectedId,
            Constant.VISIBILITY_SETTING_TYPE.PROJECT,
            definitionView
        )
    ]);

    if (Utils.isEmptyArray(privateViews) && Utils.isEmptyArray(projectViews)) {
        setIsInitView(false);
        return [];
    }

    let listView = sortListViewByOrder([...privateViews, ...projectViews], userId);
    listView.forEach(view => {
        view.isEditName = false;
        view.isMove = false;
    });

    let listViewShow = listView.filter(view => view.status === Constant.STATUS_VIEW.SHOW);
    if (Utils.isEmptyArray(listViewShow)) {
        setIsInitView(false);
        return [];
    }

    let newStructure = {
        ...JSON.parse(listViewShow[0].structure),
        visibility: visibleType
    };

    if (!Utils.isEmptyObject(getDefaultDrilldownParams)) {
        const corporationId = getDefaultDrilldownParams?.corporationId;
        const setDefaultDrillDownData = getDefaultDrilldownParams?.setDefaultDrillDownData;
        if (corporationId && setDefaultDrillDownData) {
            await getDefaultDrilldownData(API, visibleType, newStructure, corporationId, projectSelectedId, userId, setDefaultDrillDownData);
        }
    }

    setListView(listView);
    setViewStructure(newStructure);
    setViewSelected(listViewShow[0]);
    setIsInitView(false);
    return listViewShow;
};


/**
 * getDefaultDrilldownData
 * get default drilldown data
 * @param {*} API 
 * @param {*} visibleType 
 * @param {*} structure 
 * @param {*} corporationId 
 * @param {*} projectSelectedId 
 * @param {*} userId 
 * @param {*} fieldListInState 
 * @param {*} setDefaultDrillDownData 
 */
export const getDefaultDrilldownData = async (API, visibleType, structure, corporationId, projectSelectedId, userId, setDefaultDrillDownData) => {
    let defaultViewStructure = cloneDeep(Constant.DEFAULT_VIEW_STRUCTURE);
    defaultViewStructure.visibility = visibleType;
    defaultViewStructure.ticketProperty = structure.ticketProperty
        ? structure.ticketProperty
        : '';

    let resDefaultDrillDownData = await getHelpdeskDrilldownDataAndSugguestionList(
        API,
        defaultViewStructure,
        corporationId,
        projectSelectedId,
        userId,
        [],
        {isDefault: true}
    );
    setDefaultDrillDownData(resDefaultDrillDownData.drillDownData);
}

export const createNewView = async argsCommon => {    
    let listView = argsCommon.listView;
    let visibleType = argsCommon.viewStructure.visibility;

    //check user role: user is member of projectSelected => create only visibility private
    if (!argsCommon.isProjectManager) {
        visibleType = Constant.VISIBILITY_SETTING_TYPE.PRIVATE;
    }

    let visibleRange = getVisibleRangeByVisibleType(visibleType, argsCommon.userDetail.id, argsCommon.projectInuseDetail.id);

    // Remove visibility option;
    let viewStructure = cloneDeep(argsCommon.viewStructure);
    // viewStructure.ticketProperty = "";
    delete viewStructure.visibility;

    // validate new viewName
    let newName = generateNameForNewView(listView);
    let listViewLasted = await getListViewByVisibleRange(
        argsCommon.API,
        argsCommon.projectInuseDetail.id,
        visibleRange,
        argsCommon.viewStructure.visibility,
        Constant.DEFINITION_VIEW.HELPDESK
    );

    let listCheck = listViewLasted.filter(v => {
        let viewNameObj = JSON.parse(v.name);
        let viewName = viewNameObj['jp'].text;
        viewName = viewName.trim().toLowerCase();

        return viewName.localeCompare(newName.toLowerCase()) === 0;
    });
    if (listCheck?.length) {
        argsCommon.setIsOpenDialogViewChanged(true);
        return;
    }

    let createViewDto = {
        name: JSON.stringify({
            jp: {
                A: 'F',
                text: newName
            }
        }),
        affiliationOrganization: argsCommon.corporationDetail.id,
        affiliationProject: argsCommon.projectInuseDetail.id,
        affiliationView: '',
        visibleRange: visibleRange,
        visibleType: visibleType,
        structure: viewStructure ? JSON.stringify(viewStructure) : JSON.stringify({}),
        status: Constant.STATUS_VIEW.SHOW,
        viewType: Constant.VIEW_TYPE.CUSTOM,
        order: generateOrderForNewForm(listView),
        definitionView: Constant.DEFINITION_VIEW.HELPDESK,
        creator: argsCommon.userDetail.id,
        sortData: JSON.stringify({
            [argsCommon.userDetail.id]: generateSortDataForNewForm(
                listView,
                argsCommon.userDetail.id
            )
        })
    };

    let createViewResult = await createViewData(argsCommon.API, createViewDto);

    if (createViewResult && visibleType === argsCommon.viewStructure.visibility) {
        //update list view
        let listViewClone = cloneDeep(argsCommon.listView);
        listViewClone.push(createViewResult);

        argsCommon.setListView(listViewClone);
        //update view selected is new view
        argsCommon.setViewSelected(createViewResult);
    }
};

export const generateNameForNewView = listView => {
    let newViewName = '';
    let viewNameDefault = Constant.VIEW_NAME_DEFAULT;

    if (Utils.isEmptyArray(listView)) {
        newViewName = `${viewNameDefault} 1`;
        return newViewName;
    }

    let maxIndex = BigInt(0);
    let regex = new RegExp(`^${viewNameDefault} (\\d*)+$`, 'g');

    for (let i = 0; i < listView.length; i++) {
        let view = listView[i];
        let viewNameObj = JSON.parse(view?.name);
        if (!viewNameObj || typeof viewNameObj !== 'object') continue;
        let viewName = viewNameObj['jp'].text;
        if (Utils.isEmptyString(viewName)) continue;

        let nameMatch = viewName.match(regex);
        if (Utils.isEmptyArray(nameMatch)) continue;

        let indexName = nameMatch[0].replace(`${Constant.VIEW_NAME_DEFAULT} `, '');
        if (Utils.isEmptyString(indexName)) continue;

        let firstChar = indexName.charAt(0);
        if (firstChar === '0') continue;

        indexName = BigInt(indexName);
        if (indexName && indexName > maxIndex) {
            maxIndex = indexName;
        }
    }

    newViewName = `${Constant.VIEW_NAME_DEFAULT} ${maxIndex + 1n}`;

    return newViewName;
};

export const generateOrderForNewForm = listView => {
    if (Utils.isEmptyArray(listView)) {
        return 1;
    }
    let maxOrder = Math.max.apply(
        Math,
        listView.map(view => {
            let order = view.order;
            return order ? order : 0;
        })
    );

    return maxOrder + 1;
};

export const generateSortDataForNewForm = (listView, userId) => {
    if (Utils.isEmptyArray(listView)) {
        return 1;
    }

    return JSON.parse(listView[listView.length - 1].sortData)[userId] + 1;
};

/**
 * onHandlerDeleteViewHelpdeskList
 * handle delete view helpdesk list
 * @param {*} API
 * @param {*} viewDelete
 * @param {*} setIsConfirmDeleteView
 * @param {*} setIsLoadingView
 * @param {*} listView
 * @param {*} setListView
 * @param {*} viewSelected
 * @param {*} setViewSelected
 * @param {*} viewStructure
 * @param {*} setViewStructure
 */
export const onHandlerDeleteViewHelpdeskList = async (
    API,
    viewDelete,
    setIsConfirmDeleteView,
    setIsLoadingView,
    listView,
    setListView,
    viewSelected,
    setViewSelected,
    viewStructure,
    setViewStructure
) => {
    setIsConfirmDeleteView(false);
    setIsLoadingView(true);
    // deleteViewData
    let listViewClone = cloneDeep(listView);
    let listViewShow = listViewClone.filter(view => view.status === Constant.STATUS_VIEW.SHOW);

    // delete view in listView
    let indexView = listViewClone.findIndex(view => view.id === viewDelete.id);
    if (indexView !== -1) {
        listViewClone.splice(indexView, 1);
    }
    setListView(listViewClone);

    // delete view in DB
    await deleteViewData(API, viewDelete.id);
    setIsLoadingView(false);

    let indexViewShow = listViewShow.findIndex(view => view.id === viewDelete.id);
    // check case delete current view
    if (viewSelected.id === viewDelete.id && listViewShow.length > 1) {
        if (indexViewShow === 0) {
            let newViewSelected = listViewShow[1];
            if (!Utils.isEmptyObject(newViewSelected)) {

                let currentVisibility = viewStructure.visibility;
                let newView = {
                    ...JSON.parse(newViewSelected.structure),
                    visibility: currentVisibility
                };
                setViewStructure(newView);
                setViewSelected(newViewSelected);
            }
        } else {
            let newViewSelected = listViewShow[indexViewShow - 1];
            if (!Utils.isEmptyObject(newViewSelected)) {
                

                let currentVisibility = viewStructure.visibility;
                let newView = {
                    ...JSON.parse(newViewSelected.structure),
                    visibility: currentVisibility
                };
                setViewStructure(newView);
                setViewSelected(newViewSelected);
            }
        }
    }
};

/**
 * onHandlerHideViewHelpdeskList
 * handle hide view helpdesk list
 * @param {*} API
 * @param {*} viewHide
 * @param {*} setIsConfirmHideView
 * @param {*} setIsLoadingView
 * @param {*} listView
 * @param {*} setListView
 * @param {*} viewSelected
 * @param {*} setViewSelected
 * @param {*} viewStructure
 * @param {*} setViewStructure
 */
export const onHandlerHideViewHelpdeskList = async (
    API,
    viewHide,
    setIsConfirmHideView,
    setIsLoadingView,
    listView,
    setListView,
    viewSelected,
    setViewSelected,
    viewStructure,
    setViewStructure
) => {
    setIsConfirmHideView(false);
    setIsLoadingView(true);

    let listViewClone = cloneDeep(listView);
    let listViewShow = listViewClone.filter(view => view.status === Constant.STATUS_VIEW.SHOW);

    //update view in DB
    await updateViewData(API, viewHide.id, { status: Constant.STATUS_VIEW.HIDDEN });

    let indexView = listViewClone.findIndex(view => view.id === viewHide.id);
    if (indexView !== -1) {
        listViewClone[indexView].status = Constant.STATUS_VIEW.HIDDEN;
    }
    setListView(listViewClone);
    setIsLoadingView(false);

    let indexViewShow = listViewShow.findIndex(view => view.id === viewHide.id);
    //check case hide current view
    if (viewSelected.id === viewHide.id) {
        if (indexViewShow === 0 && listViewShow.length === 1) {
            setViewSelected({});
            let helpdeskTicketListDefaultViewStructure = cloneDeep(Constant.DEFAULT_VIEW_STRUCTURE);
            helpdeskTicketListDefaultViewStructure.options.sort =
                Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.UPDATED_AT;
            helpdeskTicketListDefaultViewStructure.ticketProperty = '';

            setViewStructure(helpdeskTicketListDefaultViewStructure);
        } else if (indexViewShow === 0 && listViewShow.length > 1) {
            let newViewSelected = listViewShow[1];
            if (!Utils.isEmptyObject(newViewSelected)) {
                setViewSelected(newViewSelected);

                let currentVisibility = viewStructure.visibility;
                let newView = {
                    ...JSON.parse(newViewSelected.structure),
                    visibility: currentVisibility
                };
                setViewStructure(newView);
            }
        } else if (indexViewShow !== 0 && listViewShow.length > 1) {
            let newViewSelected = listViewShow[indexViewShow - 1];
            if (!Utils.isEmptyObject(newViewSelected)) {
                setViewSelected(newViewSelected);

                let currentVisibility = viewStructure.visibility;
                let newView = {
                    ...JSON.parse(newViewSelected.structure),
                    visibility: currentVisibility
                };
                setViewStructure(newView);
            }
        }
    }
};


/**
 * onHandlerShowView
 * show hide view
 * @param {*} viewShow 
 * @param {*} argsCommon 
 * @param {*} setAddViewPopover 
 * @returns 
 */
export const onHandlerShowView = async (viewShow, argsCommon, setAddViewPopover) => {
    setAddViewPopover(null);
    argsCommon.setIsLoadingView(true);

    //check view was changed
    let isViewChanged = await isViewWasChanged(viewShow, argsCommon);
    if (isViewChanged) {
        argsCommon.setIsOpenDialogViewChanged(true);
        return;
    }

    let listViewClone = cloneDeep(argsCommon.listView);

    let indexView = listViewClone.findIndex(view => view.id === viewShow.id);
    if (indexView !== -1) {
        listViewClone[indexView].status = Constant.STATUS_VIEW.SHOW;
    }

    let newStructure = Utils.convertJSONDataToObject(listViewClone[indexView].structure);
    // let listViewShow = listViewClone.filter(view => view.status === Constant.STATUS_VIEW.SHOW);
    // if (listViewShow.length === 1) {
    let currentVisibility = argsCommon.viewStructure.visibility;
    newStructure.visibility = currentVisibility;  
    // }
    argsCommon.setViewStructure(newStructure);

    //update view in DB
    await updateViewData(argsCommon.API, viewShow.id, { status: Constant.STATUS_VIEW.SHOW });

    let listViewHide = listViewClone.filter(view => view.status === Constant.STATUS_VIEW.HIDDEN);
    if (listViewHide.length === 0) {
        argsCommon.setOpenListViewHide(false);
    }

    argsCommon.setListView(listViewClone);
    argsCommon.setIsLoadingView(false);
    argsCommon.setViewSelected(listViewClone[indexView]);
    
    // Scroll to the view that was just shown
    scrollToView(viewShow?.id);
    
    // argsCommon.setTriggerSearch(Date.now().toString());
};

/**
 * onDoubleClickViewHelpdeskList
 * on click edit view name
 * @param {*} view
 * @param {*} isCustomer
 * @param {*} viewStructure
 * @param {*} isProjectManager
 * @param {*} listView
 * @param {*} setListView
 * @returns
 */
export const onDoubleClickViewHelpdeskList = async (
    view,
    isCustomer,
    viewStructure,
    isProjectManager,
    listView,
    setListView,
    clickTimeout,
    setClickTimeout,
    userDetail
) => {
    clearTimeout(clickTimeout);
    setClickTimeout(null);

    if (isCustomer || view.viewType === Constant.VIEW_TYPE.DEFAULT) return;

    if (!isProjectManager && view.creator !== userDetail.id) {
        return;
    }

    let listViewClone = cloneDeep(listView);
    let index = listViewClone.findIndex(v => v.id === view.id);

    if (index !== -1) {
        listViewClone[index].isEditName = true;
        let currentName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, view?.name);
        if (!isValidViewName(currentName)) {
            listViewClone[index].isError = true;
        }
    }

    setListView(listViewClone);
};

export const onEnterViewName = async (e, view, argsCommon) => {
    if (e.key === 'Enter') {
        let newName = e.target.value;

        await onEditViewName(view, newName, argsCommon);
    }
};

export const onEditViewRange = async ({
    userId,
    view,
    visibleType,
    visibleRange,
    setIsLoadingView,
    setListView,
    listView,
    setIsConfirmChangeRange,
    projectInuseDetail,
    setViewNameErrorType,
    setIsOpenDialogViewNameError,
    setCurrentRange
}) => {
    setIsConfirmChangeRange(null);
    setIsLoadingView(true);

    const viewDetail = await getViewById(null, view.id);
    if (
        Utils.isEmptyObject(viewDetail) ||
        viewDetail.visibleType === visibleType ||
        (visibleType === Constant.VISIBILITY_SETTING_TYPE.PRIVATE && viewDetail.creator !== userId)
    ) {
        setIsLoadingView(false);
        return;
    }

    const [privateViews, projectViews] = await Promise.all([
        getListViewByVisibleRange(
            null,
            projectInuseDetail.id,
            userId,
            Constant.VISIBILITY_SETTING_TYPE.PRIVATE,
            Constant.DEFINITION_VIEW.HELPDESK
        ),
        getListViewByVisibleRange(
            null,
            projectInuseDetail.id,
            projectInuseDetail.id,
            Constant.VISIBILITY_SETTING_TYPE.PROJECT,
            Constant.DEFINITION_VIEW.HELPDESK
        )
    ]);

    const viewCurrentName = Utils.convertJSONDataToObject(view.name)
        ['jp']['text'].trim()
        .toLowerCase();

    const listCheck = [...privateViews, ...projectViews].filter(v => {
        const viewName = Utils.convertJSONDataToObject(v.name)
            ['jp']['text'].trim()
            .toLowerCase();
        return viewName.localeCompare(viewCurrentName) === 0 && v.id !== view.id;
    });

    if (!Utils.isEmptyArray(listCheck)) {
        setIsLoadingView(false);
        setViewNameErrorType(Constant.EDIT_VIEW_NAME_ERROR_TYPE.DUPLICATE);
        setIsOpenDialogViewNameError(true);
        setCurrentRange(view.visibleType);
        return;
    }

    await updateViewData(null, view.id, {
        visibleType,
        visibleRange,
        updator: userId
    });

    let listViewClone = cloneDeep(listView);
    const index = listViewClone.findIndex(v => v.id === view.id);
    listViewClone[index].visibleType = visibleType;
    listViewClone[index].visibleRange = visibleRange;
    setListView(listViewClone);

    setIsLoadingView(false);
};

export const onBlurViewName = async (e, view, argsCommon) => {
    let newName = e.target.value;
    await onEditViewName(view, newName, argsCommon);
};


const onEditViewName = async (view, newName, argsCommon) => {
    argsCommon.setIsLoadingView(true);
    //check view was changed
    let viewDetailLatest = await getViewById(argsCommon.API, view.id);
    if (Utils.isEmptyObject(viewDetailLatest)) {
        argsCommon.setIsOpenDialogViewChanged(true);
        argsCommon.setIsLoadingView(false);
        return;
    }

    newName = newName.trim();

    let listViewClone = cloneDeep(argsCommon.listView);
    let index = listViewClone.findIndex(v => v.id === view.id);

    // check view name blank
    if (Utils.isEmptyString(newName)) {
        argsCommon.setIsLoadingView(false);
        argsCommon.setViewNameErrorType(Constant.EDIT_VIEW_NAME_ERROR_TYPE.BLANK);
        argsCommon.setIsOpenDialogViewNameError(true);

        listViewClone[index].isError = true;
        argsCommon.setListView(listViewClone);

        return;
    }

    // check view name max length
    if (Utils.countCharacters(newName) > Constant.MAX_LENGTH.VIEW_NAME) {
        argsCommon.setIsLoadingView(false);
        argsCommon.setViewNameErrorType(Constant.EDIT_VIEW_NAME_ERROR_TYPE.MAX_LENGTH);
        argsCommon.setIsOpenDialogViewNameError(true);

        listViewClone[index].isError = true;
        argsCommon.setListView(listViewClone);

        return;
    }

    // check view name not change
    let currentNameObj = Utils.convertJSONDataToObject(view.name);
    let currentName = currentNameObj['jp'].text;
    currentName = currentName.trim().toLowerCase();
    if (currentName.localeCompare(newName.toLowerCase()) === 0) {
        argsCommon.setIsLoadingView(false);
        listViewClone[index].isEditName = false;
        listViewClone[index].isError = false;
        argsCommon.setListView(listViewClone);
        return;
    }

    let visibleType = argsCommon.viewStructure.visibility;
    let visibleRange = getVisibleRangeByVisibleType(visibleType, argsCommon.userDetail.id, argsCommon.projectInuseDetail.id);

    let listView = await getListViewByVisibleRange(
        argsCommon.API,
        argsCommon.projectInuseDetail.id,
        visibleRange,
        argsCommon.viewStructure.visibility,
        Constant.DEFINITION_VIEW.HELPDESK
    );

    let listCheck = listView.filter(v => {
        let viewNameObj = JSON.parse(v.name);
        let viewName = viewNameObj['jp'].text;
        viewName = viewName.trim().toLowerCase();

        return viewName.localeCompare(newName.toLowerCase()) === 0 && v.id !== view.id;
    });

    if (!Utils.isEmptyArray(listCheck)) {
        argsCommon.setIsLoadingView(false);
        argsCommon.setViewNameErrorType(Constant.EDIT_VIEW_NAME_ERROR_TYPE.DUPLICATE);
        argsCommon.setIsOpenDialogViewNameError(true);

        listViewClone[index].isError = true;
        argsCommon.setListView(listViewClone);
        return;
    }

    // update DB
    await updateViewData(argsCommon.API, view.id, {
        name: JSON.stringify({
            jp: {
                A: 'F',
                text: newName
            }
        })
    });

    if (index !== -1) {
        listViewClone[index].name = JSON.stringify({
            jp: {
                A: 'F',
                text: newName
            }
        });
        listViewClone[index].isEditName = false;
        listViewClone[index].isError = false;

        argsCommon.setListView(listViewClone);
    }

    argsCommon.setIsLoadingView(false);
};

/**
 * saveSearchCondition
 * @param {*} API 
 * @param {*} view 
 * @param {*} setIsLoadingView 
 * @param {*} viewStructure 
 * @param {*} listView 
 * @param {*} setListView 
 * @param {*} viewSelected 
 * @param {*} setViewSelected 
 */
export const saveSearchCondition = async (
    API,
    view,
    setIsLoadingView,
    viewStructure,
    listView,
    setListView,
    viewSelected,
    setViewSelected
) => {
    setIsLoadingView(true);

    //update view in DB
    await updateViewData(API, view.id, {
        structure: viewStructure ? JSON.stringify(viewStructure) : JSON.stringify({})
    });

    //update view in listView
    let listViewClone = cloneDeep(listView);
    let index = listViewClone.findIndex(v => v.id === view.id);
    listViewClone[index].structure = viewStructure
        ? JSON.stringify(viewStructure)
        : JSON.stringify({});
    setListView(listViewClone);

    //update viewSelected
    let viewSelectedClone = cloneDeep(viewSelected);
    viewSelectedClone.structure = viewStructure
        ? JSON.stringify(viewStructure)
        : JSON.stringify({});
    setViewSelected(viewSelectedClone);

    setIsLoadingView(false);
};

/**
 * isViewWasChanged
 * @param {*} view
 * @param {*} argsCommon
 * @returns
 */
export const isViewWasChanged = async (view, argsCommon) => {
    argsCommon.setIsLoadingView(true);
    let viewDetailLatest = await getViewById(argsCommon.API, view.id);
    argsCommon.setIsLoadingView(false);

    // check change view
    let isChangeView =
        Utils.isEmptyObject(viewDetailLatest) || viewDetailLatest.status !== view.status;
    return isChangeView;
};

/**
 * onClickOpenMoveView
 * @param {*} view 
 * @param {*} argsCommon 
 */
export const onClickOpenMoveView = (view, argsCommon) => {
    let listViewClone = cloneDeep(argsCommon.listView);

    // only one view isMove
    listViewClone.forEach(thisView => {
        if (thisView.id === view.id) {
            thisView.isMove = true;
        } else {
            thisView.isMove = false;
        }
    });

    argsCommon.setListView(listViewClone);
};

export const onChangeVisibilitySetting = async (
    API, 
    setIsInitView, 
    setViewSelected, 
    viewStructure,
    setViewStructure, 
    setListView,
    userId,
    projectId,
    setIsLoadingView,
    visibilitySettingValue,
    corporationId,
    setDefaultDrillDownData
) => {
    setIsLoadingView(true);

    const getDefaultDrilldownParams = {
        corporationId,
        setDefaultDrillDownData
    }

    await getHelpDeskListViews(
        API, 
        setIsInitView, 
        setViewSelected, 
        setViewStructure, 
        setListView, 
        userId,
        projectId,
        visibilitySettingValue,
        Constant.DEFINITION_VIEW.HELPDESK,
        getDefaultDrilldownParams
    );
    setIsLoadingView(false);
};

/**
 * onChangeViewSelectedHelpdeskList
 * on click change view helpdesk list
 * @param {*} view
 * @param {*} viewSelected
 * @param {*} setViewSelected
 * @param {*} viewStructure
 * @param {*} setViewStructure
 * @param {*} setTriggerSearch
 * @returns
 */
export const onChangeViewSelectedHelpdeskList = (
    view,
    viewSelected,
    setViewSelected,
    viewStructure,
    setViewStructure,
    setTriggerSearch,
    clickTimeout,
    setClickTimeout
) => {
    if (clickTimeout) {
        clearTimeout(clickTimeout);
        setClickTimeout(null);
        return;
    }

    const loadingChangeViewSelected = () => {
        setViewSelected(view);
        let currentVisibility = viewStructure.visibility;
        let newViewStructure = {
            ...Utils.convertJSONDataToObject(view.structure),
            visibility: currentVisibility
        };
        setViewStructure(newViewStructure);
        setTriggerSearch(Date.now().toString());
        setClickTimeout(null);
    };

    if (view.id === viewSelected.id) {
        //handle set timeout to cover for click and double click in same element
        const timeout = setTimeout(() => {
            loadingChangeViewSelected();
        }, 200);
        setClickTimeout(timeout);
    } else {
        loadingChangeViewSelected();
    }
};

/**
 * onBeforeDragStartHandler
 * @param {*} event 
 * @param {*} argsCommon 
 */
export const onBeforeDragStartHandler = (event, argsCommon) => {
    //disable slide before drag start
    let newSetting = { ...argsCommon.settingSlideRecoilState };
    newSetting.disable = true;

    argsCommon.setSettingSlideRecoilState(newSetting);
};


/**
 * onDragViewEnd
 * @param {*} result 
 * @param {*} argsCommon 
 * @returns 
 */
export const onDragViewEnd = async (result, argsCommon) => {
    if (!result.destination) return;
    let listViewClone = cloneDeep(argsCommon.listView);
    let listViewShow = listViewClone.filter(v => v.status === Constant.STATUS_VIEW.SHOW);

    // get index in listViewShow
    let sourceIndex = result.source.index;
    let desIndex = result.destination.index;

    // get item by index in listViewShow
    let itemSource = listViewShow[sourceIndex];
    let itemDes = listViewShow[desIndex];

    // get index in listView
    let sourceIndexListView = listViewClone.findIndex(v => v.id === itemSource.id);
    let desIndexListView = listViewClone.findIndex(v => v.id === itemDes.id);

    // move view in listView
    let [viewMove] = listViewClone.splice(sourceIndexListView, 1);
    viewMove.isMove = false;
    listViewClone.splice(desIndexListView, 0, viewMove);

    // update order in listView
    let order = 1;
    listViewClone.forEach(view => {
        view.order = order;
        order++;
    });
    await argsCommon.setListView(listViewClone);

    // update view in DB
    argsCommon.setIsLoadingView(true);
    Promise.all(
        listViewClone.map(view => {
            return updateViewData(argsCommon.API, view.id, {
                order: view.order,
                sortData: JSON.stringify({
                    ...JSON.parse(view.sortData),
                    [argsCommon.userDetail.id]: view.order
                })
            });
        })
    );

    argsCommon.setIsLoadingView(false);

    // Scroll to the view that was just moved
    // Wait for DOM to update after setListView
    setTimeout(() => {
        const viewElement = document.querySelector(`[data-view-id="${viewMove.id}"]`);
        if (viewElement) {
            const container = document.querySelector('#container');
            if (container) {
                const elementRect = viewElement.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                
                // Check if view is already visible
                if (elementRect.left < containerRect.left || 
                    elementRect.right > containerRect.right) {
                    // Scroll to center the moved view
                    const scrollLeft = container.scrollLeft + 
                        (elementRect.left - containerRect.left) - 
                        (containerRect.width / 2) + 
                        (elementRect.width / 2);
                    
                    container.scrollTo({
                        left: scrollLeft,
                        behavior: 'smooth'
                    });
                }
            }
        }
    }, 100);
};
